import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DealerAlert, DealerService } from '@app-services/dealer.service';
import { MakeModel, OpenService, VehicleFilter } from '@app-services/open.service';
import { SharedAnimations } from '@app-shared/animations/shared-animations';
import { Shelf } from '@app-model/shelf';
import { MarketPlacePersonalizationUtils } from '@app-components/layouts/shopkeeper-layout/marketplace-personalization';
import { inject } from '@angular/core';
import { PurchaseProfileService } from '@app/views/private/shopkeeper/cast-off/purchase-profile/purchase-profile.service';
import { Vehicle } from '@app-model/vehicle';

@Component({
  selector: 'app-cast-off-home',
  templateUrl: './cast-off-home.component.html',
  styleUrls: ['./cast-off-home.component.scss'],
  animations: [SharedAnimations]
})
export class CastOffHomeComponent implements OnInit {
  readonly purchaseProfileService = inject(PurchaseProfileService);

  readonly segmentConfig: { value: string; label: string; icon: string }[] = [
    { value: 'CAR', label: 'Leve', icon: 'fa-solid fa-car-side' },
    { value: 'TRUCK', label: 'Pesado', icon: 'fa-solid fa-truck' },
    { value: 'MOTORCYCLE', label: 'Moto', icon: 'fa-solid fa-motorcycle' },
  ];

  savedFilters: DealerAlert[];
  shelves: Shelf[] = [];
  selectedSegment: "ALL" | "CAR" | "TRUCK" | "MOTORCYCLE" = "ALL";
  activeSegments: string[] = this.segmentConfig.map(s => s.value);
  segmentsLoaded = false;

  makeModels: MakeModel[] = [];
  filteredMakeModels: any[] = [];
  searchText = '';
  showDropdown = false;

  filter: VehicleFilter = new VehicleFilter();
  totalElements: number;

  constructor(
    private openService: OpenService,
    private dealerService: DealerService,
    private elRef: ElementRef,
    private router: Router
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-wrapper')) {
      this.showDropdown = false;
    }
  }

  ngOnInit() {
    this.openService.getLayoutVars().subscribe(layoutVars => {
      MarketPlacePersonalizationUtils.initCssVars(this.elRef, layoutVars);
    })
    
    this.initShelves();
    this.initSavedFilters();
    this.initFilterOptions();
  }

  private initFilterOptions(): void {
    this.filter.pageControl = {
      page: 0,
      pageSize: 24,
      param: '',
      value: ''
    };
    this.filter.statuses = ['ACTIVE', 'FAST_QUOTE', 'WAITING_PUBLICATION'];
    this.filter.dealTypes = ['DEFAULT', 'FAST_QUOTE', 'SHOWROOM'];

    this.openService.getHomeFilterOptions(this.filter).subscribe(resp => {
      this.makeModels = resp.makeModels;
      this.totalElements = resp.totalElements;

      const segments = resp.segmentOptions
        ? (Array.isArray(resp.segmentOptions) ? resp.segmentOptions.map(s => s.id) : Object.keys(resp.segmentOptions).filter(k => resp.segmentOptions[k] > 0))
        : resp.makeModels?.map(m => m.segment);

      const foundSegments = [...new Set(segments?.filter(s => !!s).map(s => s.toUpperCase()))];
      if (!this.filter.segments?.length) {
        this.activeSegments = foundSegments.length > 0 ? foundSegments : this.segmentConfig.map(s => s.value);
      }
      this.segmentsLoaded = true;

      if (this.selectedSegment === 'ALL' && this.activeSegments.length === 1) {
        this.selectedSegment = this.activeSegments[0] as any;
      }

      this.filteredMakeModels = [...this.makeModels];
    })
  }

  private initShelves(): void {
    this.openService.getShelves().subscribe(resp => {
      this.shelves = resp;
    })
  }

  private initSavedFilters(): void {
    this.dealerService.getAlerts().subscribe(resp => {
      this.savedFilters = resp;
    })
  }

  selectSegment(value: "ALL" | "CAR" | "TRUCK" | "MOTORCYCLE"): void {
    if (this.selectedSegment === value) return;

    this.selectedSegment = value;
    this.filter.segments = value === 'ALL' ? [] : [value];
    this.initFilterOptions();
  }

  submitFilter(filter: VehicleFilter, isFastQuote?: boolean): void {
    const redirectTo = isFastQuote ? ["cotacao-rapida", "veiculos"] : ["arrematar", "veiculos"];
    this.router.navigate(redirectTo, {
      queryParams: filter.toParamMap(),
      replaceUrl: true
    });
  }

  onSearchTextChange(): void {
    const search = this.searchText.toLowerCase();
    if (!search) {
      this.filteredMakeModels = [...this.makeModels];
    } else {
      this.filteredMakeModels = this.makeModels
        .map(make => ({
          ...make,
          models: make.name.toLowerCase().includes(search)
            ? make.models
            : make.models.filter(model => model.name.toLowerCase().includes(search))
        }))
        .filter(group => group.name.toLowerCase().includes(search) || group.models.length > 0);
    }
  }

  onInputFocus(): void {
    this.showDropdown = true;
    this.filteredMakeModels = [...this.makeModels];
  }

  selectMake(makeId: string): void {
    this.filter.makeIds = [makeId];
    this.submitFilter(this.filter);
  }

  selectModel(modelId: string): void {
    this.filter.modelIds = [modelId];
    this.submitFilter(this.filter);
  }

  openPurchaseProfile(step?: number): void {
    this.purchaseProfileService.openModal(step);
  }
}
